/**
 * Compress image to target size while maintaining 16:9 aspect ratio
 * @param {File} file - Original image file
 * @param {number} maxSizeMB - Maximum file size in MB (default: 1)
 * @param {number} targetAspectRatio - Target aspect ratio (default: 16/9)
 * @returns {Promise<File>} Compressed image file
 */
export const compressImageToSize = async (file, maxSizeMB = 1, targetAspectRatio = 16 / 9) => {
  return new Promise((resolve, reject) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
    const img = new Image();
    
    img.onload = () => {
      // Calculate dimensions maintaining 16:9 aspect ratio
      const currentAspectRatio = img.width / img.height;
      let canvasWidth, canvasHeight;
      let sourceX = 0, sourceY = 0;
      let sourceWidth = img.width, sourceHeight = img.height;
      
      // If aspect ratio doesn't match, crop to 16:9
      if (Math.abs(currentAspectRatio - targetAspectRatio) > 0.01) {
        if (currentAspectRatio > targetAspectRatio) {
          // Image is wider than 16:9, crop width (keep full height)
          canvasHeight = img.height;
          canvasWidth = canvasHeight * targetAspectRatio;
          sourceX = (img.width - canvasWidth) / 2;
          sourceWidth = canvasWidth;
        } else {
          // Image is taller than 16:9, crop height (keep full width)
          canvasWidth = img.width;
          canvasHeight = canvasWidth / targetAspectRatio;
          sourceY = (img.height - canvasHeight) / 2;
          sourceHeight = canvasHeight;
        }
      } else {
        // Already 16:9, use original dimensions
        canvasWidth = img.width;
        canvasHeight = img.height;
      }
      
      // Create canvas with 16:9 dimensions
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      
      // Draw image on canvas (cropped to 16:9 if needed)
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvasWidth,
        canvasHeight
      );
      
      // Compress image with quality adjustment
      let quality = 0.9;
      const compress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            // If blob is still too large, reduce quality and try again
            if (blob.size > maxSizeBytes && quality > 0.1) {
              quality -= 0.1;
              compress();
            } else {
              // Create a new File from the blob
              const compressedFile = new File(
                [blob],
                file.name,
                {
                  type: file.type || 'image/jpeg',
                  lastModified: Date.now(),
                }
              );
              resolve(compressedFile);
            }
          },
          file.type || 'image/jpeg',
          quality
        );
      };
      
      compress();
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};
